/**
 * `{{…}}` completion inside Monaco.
 *
 * Every other field in the app offers the variables you have the moment you
 * type `{{`, and narrows as you keep typing (SyntaxHighlightInput). The code
 * editors — request bodies, scripts, GraphQL — offered only the generated `$`
 * names, and only after you had typed the `$`. So the one place you are most
 * likely to be composing a reference by hand was the one place that would not
 * help you do it (owner, 23.09.2026).
 *
 * The list itself is `utils/variableSuggestions`, unchanged: same ordering,
 * same scope precedence, same one-entry-per-shadowed-name rule. What is here is
 * only the two things Monaco needs and the others do not — reading the caret
 * out of a line of text, and shaping an item Monaco will render.
 *
 * Split out of CodeEditor because the provider is registered ONCE per Monaco
 * instance (it is global to the language, not to an editor), while the
 * variables it offers belong to whichever editor you are typing in. That
 * mismatch is the whole reason this needs a module with a test.
 */
import { suggestionsFor, SUGGEST_ALL } from './variableSuggestions';
import { DYNAMIC_VARIABLE_DOCS } from './dynamicVariables';

/**
 * The scopes the FOCUSED editor resolves against, and whose they are.
 *
 * One provider serves every mounted editor, so it cannot close over any of
 * them — the editor with the caret says who it is. The OWNER half is not
 * ceremony: a request tab mounts three of these (body, pre-request script, test
 * script), and when each published its scopes on mount the last one to mount
 * won. The script editors resolve nothing — `{{…}}` is not substituted inside a
 * script, `runScript` gets the text verbatim — so they published empty scopes
 * over the body editor's, and `{{` in the body offered nothing but the
 * generated names (owner, 23.09.2026: "its not showing the collection ones").
 *
 * Focus decides. Mounting does not.
 */
let activeScopes = {};
let activeOwner = null;

/** Take ownership — the editor was focused. */
export function claimMonacoVariableScopes(owner, scopes) {
  activeOwner = owner;
  activeScopes = scopes || {};
}

/**
 * Refresh the scopes, but only for the editor that currently owns them.
 *
 * Variables arrive asynchronously and change while you are typing — adding one
 * in the Variables panel and going back to the body editor must not leave the
 * list stale. An editor that does NOT have the caret updating here is the
 * clobbering this exists to prevent.
 */
export function updateMonacoVariableScopes(owner, scopes) {
  if (owner !== activeOwner) return;
  activeScopes = scopes || {};
}

/** Give it up on unmount, so a closed tab does not keep answering. */
export function releaseMonacoVariableScopes(owner) {
  if (owner !== activeOwner) return;
  activeOwner = null;
  activeScopes = {};
}

export function getMonacoVariableScopes() {
  return activeScopes;
}

/**
 * What the caret is inside, if it is inside an unclosed `{{`.
 *
 * @param {string} textBefore text on this line up to the caret
 * @returns {{prefix: string, start: number}|null} `start` is the 0-based offset
 *          of the prefix within the line — what a replacement has to overwrite.
 *          null when the caret is not in a reference at all.
 */
export function referenceAtCaret(textBefore) {
  // `[^}{]*` and not `[^}]*`: `{{a}} {{b` must match the SECOND reference, and
  // a `{` inside one means the first pair was never a reference to begin with.
  const match = /\{\{([^}{]*)$/.exec(textBefore || '');
  if (!match) return null;
  const prefix = match[1];
  // A reference name has no spaces — `{{ ` with a space is someone typing prose
  // after a brace, not a name.
  if (/\s/.test(prefix)) return null;
  return { prefix, start: match.index + 2 };
}

/**
 * The completions for a caret, or null when the list should not open.
 *
 * @param {string} textBefore  the line up to the caret
 * @param {string} textAfter   the line from the caret on — decides whether the
 *                             insertion has to supply the closing braces
 * @param {object} scopes      see utils/variableSuggestions
 */
export function completionsAt(textBefore, textAfter, scopes, include = SUGGEST_ALL) {
  const at = referenceAtCaret(textBefore);
  if (!at) return null;

  const items = suggestionsFor(at.prefix, scopes, include);
  // No matches closes the list rather than showing an empty one — the same rule
  // the other fields follow.
  if (!items.length) return null;

  // Already closed: inserting `}}` would give `{{name}}}}`.
  const closed = /^\s*\}\}/.test(textAfter || '');
  return {
    start: at.start,
    prefix: at.prefix,
    items: items.map((item) => ({
      name: item.name,
      kind: item.kind,
      detail:
        item.kind === 'dynamic'
          ? DYNAMIC_VARIABLE_DOCS[item.name] || 'Generated at request time'
          : item.detail,
      // The scope is worth saying for a saved name and meaningless for a
      // generated one.
      scope: item.scope,
      insertText: closed ? item.name : `${item.name}}}`,
    })),
  };
}

/**
 * Register the provider on a Monaco instance. Idempotent per instance.
 *
 * @param {object} monaco the live instance from beforeMount/onMount
 * @param {string[]} languages
 */
export function registerMonacoVariableCompletions(monaco, languages) {
  const provider = {
    // `{` fires on the second brace of `{{`; `$` keeps the old behaviour of
    // opening on a generated name mid-reference.
    triggerCharacters: ['{', '$'],
    provideCompletionItems: (model, position) => {
      const line = model.getLineContent(position.lineNumber);
      const textBefore = line.slice(0, position.column - 1);
      const textAfter = line.slice(position.column - 1);
      const result = completionsAt(textBefore, textAfter, activeScopes);
      if (!result) return { suggestions: [] };

      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: result.start + 1, // Monaco columns are 1-based
        endColumn: position.column,
      };

      return {
        suggestions: result.items.map((item, index) => ({
          label: item.name,
          kind:
            item.kind === 'saved'
              ? monaco.languages.CompletionItemKind.Value
              : monaco.languages.CompletionItemKind.Variable,
          detail: item.scope ? `${item.detail} · ${item.scope}` : item.detail,
          insertText: item.insertText,
          range,
          // Monaco sorts alphabetically unless told otherwise, which would put
          // the generated names above the user's own — the exact inversion the
          // suggestion order exists to prevent.
          sortText: String(index).padStart(4, '0'),
        })),
      };
    },
  };

  return languages.map((lang) => monaco.languages.registerCompletionItemProvider(lang, provider));
}

export default registerMonacoVariableCompletions;
